import { toast } from "sonner";

/**
 * Render a resilient error toast that:
 *  - stays visible for at least 10s
 *  - never truncates the message
 *  - carries a "Másolás" (Copy) action so the full backend error can be shared
 *  - also mirrors the full payload to the browser console (console.error)
 *
 * @param {string} title      – human-readable title
 * @param {object} details    – { reason, backend, status, filename, raw, error }
 */
export function notifyError(title, details = {}) {
  const {
    reason,
    backend,
    status,
    filename,
    raw,
    error,
  } = details;

  // Build the full, untruncated description shown in the toast.
  const parts = [];
  if (filename) parts.push(`Fájl: ${filename}`);
  if (status !== undefined && status !== null) parts.push(`HTTP ${status}`);
  if (reason) parts.push(`Ok: ${reason}`);
  if (backend) parts.push(`Backend: ${typeof backend === "string" ? backend : JSON.stringify(backend)}`);
  if (raw) parts.push(`Raw: ${typeof raw === "string" ? raw : JSON.stringify(raw)}`);

  const description = parts.join("\n") || "Ismeretlen hiba";
  const clipboardText = `${title}\n${description}`;

  // Always mirror to the browser console so we can inspect after the toast fades.
  console.error("[notifyError]", title, {
    reason,
    backend,
    status,
    filename,
    raw,
    error,
  });

  toast.error(title, {
    description,
    duration: 10000,
    closeButton: true,
    action: {
      label: "Másolás",
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(clipboardText);
          toast.success("Hibaüzenet vágólapra másolva");
        } catch {
          // Fallback for browsers without Clipboard API access
          const ta = document.createElement("textarea");
          ta.value = clipboardText;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          toast.success("Hibaüzenet vágólapra másolva");
        }
      },
    },
    classNames: {
      // Wider toast + wrap long messages so nothing is cut off.
      toast: "!max-w-[560px]",
      description: "!whitespace-pre-wrap !break-words",
      title: "!whitespace-pre-wrap !break-words",
    },
  });
}
